import { Badge, Button, Card, CardContent, CardDescription, CardTitle } from '@auctisync/ui';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';

type Tier = {
  name: string;
  price: string;
  priceNote?: string;
  target: string;
  bullets: string[];
  featured?: boolean;
  ctaLabel?: string;
  ctaHref?: string;
};

const TIERS: Tier[] = [
  {
    name: 'Free Trial',
    price: '$0',
    priceNote: '30 days',
    target: 'Try before buy, no card required.',
    bullets: ['Full platform access', 'All bidding features', 'Cancel anytime'],
    ctaLabel: 'Start trial',
  },
  {
    name: 'Solo',
    price: '$49',
    priceNote: '/mo',
    target: 'Hobbyist or part-time auctioneer. 1 auction/mo.',
    bullets: [
      '1 auction/mo',
      '50 lots/auction',
      '200 inventory items',
      '1 staff seat',
      '5 GB storage',
    ],
  },
  {
    name: 'Fundraiser',
    price: '$79',
    priceNote: '/mo',
    target: 'Nonprofits, gala + benefit auctions. Captures Auctria/OneCause segment.',
    bullets: [
      'Paddle raise + fund-a-need',
      'Ticketing',
      '501(c)(3) receipts',
      'Hybrid silent + live',
      'Donor CRM',
    ],
  },
  {
    name: 'Starter',
    price: '$129',
    priceNote: '/mo',
    target: 'Single auction per month, replacing DocuSign + spreadsheets.',
    bullets: [
      '4 auctions/mo',
      '250 lots each',
      '2,000 inventory',
      '3 staff seats',
      '50 GB storage',
    ],
  },
  {
    name: 'Storage',
    price: '$199',
    priceNote: '/mo',
    target: 'Self-storage operators. Bundles the full delinquency-to-auction workflow.',
    bullets: [
      '5-state lien compliance (FL/TX/AZ/GA/NC)',
      'RPost + USPS Certified Mail APIs',
      'Column.us ad placement',
      'PMS integration included',
      '8% flat buyer premium on marketplace',
    ],
  },
  {
    name: 'Growth',
    price: '$299',
    priceNote: '/mo',
    target: 'Weekly auctioneer with 1–3 staff.',
    bullets: [
      '12 auctions/mo',
      '1,000 lots each',
      '10K inventory',
      '7 staff seats',
      '250 GB storage',
    ],
  },
  {
    name: 'Pro',
    price: '$549',
    priceNote: '/mo',
    target: "Full-time auction house. DTA's natural home.",
    bullets: [
      'Unlimited auctions',
      'Unlimited lots',
      'Unlimited inventory',
      '20 staff seats',
      '1 TB storage',
      'Embeddable JS widget',
    ],
    featured: true,
    ctaLabel: 'Get started',
  },
  {
    name: 'Elite',
    price: '$899',
    priceNote: '/mo',
    target: 'Multi-location, every bell and whistle.',
    bullets: [
      'Everything in Pro',
      '50 staff seats',
      '5 TB storage',
      'Live webcast included',
      'Custom legal pages',
    ],
  },
  {
    name: 'Enterprise',
    price: '$2,500+',
    priceNote: '/mo',
    target: 'Multi-state chains, multi-site, dedicated CSM.',
    bullets: [
      'Trustee-Grade Add-On included',
      'Multi-location support',
      'Dedicated CSM',
      'Custom SLA',
    ],
    ctaLabel: 'Contact sales',
    ctaHref: '/contact',
  },
  {
    name: 'Enterprise Plus',
    price: '$5,000+',
    priceNote: '/mo',
    target: 'White-label resellers, multi-tenant within tenant, custom SLA.',
    bullets: [
      'Full white-label',
      'Multi-language',
      'Custom JS injection',
      'Reseller infrastructure',
      'Priority engineering',
    ],
    ctaLabel: 'Contact sales',
    ctaHref: '/contact',
  },
];

const PHILOSOPHY = [
  {
    title: 'No per-bid fees. Ever.',
    body: "The #1 complaint about AuctionFlex and HiBid — we don't do it. Flat monthly subscription, period.",
  },
  {
    title: 'Marketplace % only on marketplace sales',
    body: 'Direct-to-buyer sales (your own site, your own list) pay zero AuctiSync marketplace fees. GMV % only kicks in when AuctiSync drives the buyer.',
  },
  {
    title: 'Send & Sign on every tier',
    body: "Built-in e-signature replaces DocuSign. Volume gated by tier but never an upsell — it's a differentiator, not a hidden fee.",
  },
  {
    title: 'Flat 8% buyer premium on storage',
    body: "StorageTreasures runs 10–15%. We're flat 8% forever. No silent hikes.",
  },
];

export function Pricing() {
  return (
    <Layout>
      <PageHead
        title="Pricing — AuctiSync"
        description="Nine tiers from $49 to Enterprise+. Flat monthly pricing, no per-bid fees ever. Trustee-Grade Add-On available on Growth+."
        canonicalPath="/pricing"
      />

      <section className="mx-auto max-w-6xl px-6 pt-16 pb-12 md:pt-24 md:pb-16">
        <Badge className="mb-6">Pricing</Badge>
        <h1
          className="max-w-4xl text-4xl leading-tight font-bold tracking-tight md:text-6xl md:leading-[1.05]"
          style={{ color: 'var(--text)' }}
        >
          Nine tiers. One platform. Zero legacy baggage.
        </h1>
        <p className="mt-6 max-w-2xl text-lg md:text-xl" style={{ color: 'var(--text-muted)' }}>
          Flat monthly subscription. No per-bid fees, no per-lot fees, no surprise invoices.
          Marketplace % only applies when AuctiSync drives the buyer.
        </p>
      </section>

      <section
        className="border-y py-16"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
      >
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {TIERS.map((tier) => (
              <Card
                key={tier.name}
                className="h-full"
                style={
                  tier.featured ? { borderColor: 'var(--brand-accent)', borderWidth: 2 } : undefined
                }
              >
                <CardContent className="flex h-full flex-col gap-4 p-6">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    {tier.featured ? <Badge>Most popular</Badge> : null}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-4xl font-bold tracking-tight"
                      style={{ color: 'var(--text)' }}
                    >
                      {tier.price}
                    </span>
                    {tier.priceNote ? (
                      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        {tier.priceNote}
                      </span>
                    ) : null}
                  </div>
                  <CardDescription>{tier.target}</CardDescription>
                  <ul
                    className="mt-auto space-y-1.5 text-sm"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {tier.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2">
                        <span aria-hidden>→</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant={tier.featured ? 'default' : 'outline'} size="sm">
                    <a href={tier.ctaHref ?? '/contact'}>{tier.ctaLabel ?? 'Get started'}</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trustee-Grade Add-On */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-10 md:grid-cols-[1fr_2fr] md:items-center">
          <div>
            <Badge variant="success" className="mb-4">
              Cross-cutting add-on
            </Badge>
            <h2
              className="text-3xl font-bold tracking-tight md:text-4xl"
              style={{ color: 'var(--text)' }}
            >
              Trustee-Grade Add-On
            </h2>
            <p className="mt-3 text-lg font-semibold" style={{ color: 'var(--brand-accent)' }}>
              $299/mo on Growth+
            </p>
            <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              Free on Enterprise &amp; Enterprise Plus
            </p>
          </div>
          <div>
            <p className="text-lg" style={{ color: 'var(--text)' }}>
              Court-grade sale infrastructure for Chapter 7/11 bankruptcy §363 sales, plus
              platform-wide Trustee-Grade Certified badge that raises buyer confidence (and hammer
              prices) across every auction on AuctiSync.
            </p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2" style={{ color: 'var(--text-muted)' }}>
              <li>→ Sealed bid ballroom</li>
              <li>→ Stalking Horse workflow</li>
              <li>→ Qualified Bidder verification</li>
              <li>→ Higher-and-Better mechanics</li>
              <li>→ §363(f) "Free and Clear" docs</li>
              <li>→ Court report generator</li>
              <li>→ 7-year retention</li>
              <li>→ Chain-of-custody audit trail</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing philosophy */}
      <section
        className="border-t py-16"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
      >
        <div className="mx-auto max-w-6xl px-6">
          <h2
            className="mb-10 text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text)' }}
          >
            Why this pricing
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {PHILOSOPHY.map((item) => (
              <Card key={item.title} className="h-full">
                <CardContent className="flex flex-col gap-2 p-6">
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                  <CardDescription>{item.body}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="border-t py-16"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-inverted)' }}
      >
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2
            className="text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text-on-inverted)' }}
          >
            Not sure which tier fits?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Tell us your auction volume and we'll recommend the tier that pays for itself.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <a href="/contact">Talk to sales</a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="/contact">Start 30-day trial</a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
